import React, { useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, Star } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const cls = "w-full rounded-xl border border-[#2A1B1D]/12 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#D4AF37]";

const SUBTABS = ["Offers", "Packages", "Gallery", "Reviews"];

export default function ContentManager({ offers, packages, gallery, reviews, reload }) {
  const [sub, setSub] = useState("Offers");
  const [editor, setEditor] = useState(null); // {type, item}

  const save = async (type, form) => {
    try {
      const path = type;
      const payload = { ...form };
      if (type === "packages") {
        payload.includes = (Array.isArray(form.includes) ? form.includes.join(",") : form.includes || "")
          .split(",").map((s) => s.trim()).filter(Boolean);
        payload.price = Number(form.price);
      }
      if (type === "gallery") payload.order = Number(form.order || 0);
      if (form.id) await api.put(`/${path}/${form.id}`, stripId(payload));
      else await api.post(`/${path}`, stripId(payload));
      toast.success("Saved");
      setEditor(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (type, id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/${type}/${id}`);
    toast.success("Deleted");
    reload();
  };

  const toggleReview = async (r) => {
    await api.put(`/reviews/${r.id}`, { approved: !r.approved });
    reload();
  };

  return (
    <div>
      <h1 className="font-display text-4xl text-[#2A1B1D]">Content Manager</h1>
      <p className="text-sm text-[#5C4A4D] mt-1">Edit offers, packages, gallery and reviews shown on the website.</p>

      <div className="mt-6 flex gap-2">
        {SUBTABS.map((t) => (
          <button
            key={t}
            onClick={() => setSub(t)}
            data-testid={`content-tab-${t.toLowerCase()}`}
            className={`rounded-full px-4 py-2 text-xs font-medium transition-colors ${
              sub === t ? "bg-[#8E3B46] text-[#FDFBF7]" : "border border-[#2A1B1D]/12 text-[#5C4A4D] hover:border-[#8E3B46]"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {sub === "Offers" && (
        <Grid onAdd={() => setEditor({ type: "offers", item: {} })} addLabel="Add Offer">
          {offers.map((o) => (
            <Card key={o.id} image={o.image} title={o.title} sub={o.discount} testid={`admin-offer-${o.id}`}
              onEdit={() => setEditor({ type: "offers", item: o })} onDelete={() => remove("offers", o.id)} />
          ))}
        </Grid>
      )}

      {sub === "Packages" && (
        <Grid onAdd={() => setEditor({ type: "packages", item: {} })} addLabel="Add Package">
          {packages.map((p) => (
            <Card key={p.id} image={p.image} title={p.name} sub={inr(p.price)} testid={`admin-package-${p.id}`}
              onEdit={() => setEditor({ type: "packages", item: p })} onDelete={() => remove("packages", p.id)} />
          ))}
        </Grid>
      )}

      {sub === "Gallery" && (
        <Grid onAdd={() => setEditor({ type: "gallery", item: {} })} addLabel="Add Image">
          {gallery.map((g) => (
            <Card key={g.id} image={g.url} title={`Order ${g.order}`} testid={`admin-gallery-${g.id}`}
              onEdit={() => setEditor({ type: "gallery", item: g })} onDelete={() => remove("gallery", g.id)} />
          ))}
        </Grid>
      )}

      {sub === "Reviews" && (
        <div className="mt-6 rounded-2xl bg-white border border-[#2A1B1D]/8 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[#5C4A4D] bg-[#F4EFEA]/60">
                <th className="px-5 py-3">Customer</th><th className="px-5 py-3">Rating</th>
                <th className="px-5 py-3">Review</th><th className="px-5 py-3">Visible</th><th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((r) => (
                <tr key={r.id} className="border-t border-[#2A1B1D]/6" data-testid={`admin-review-${r.id}`}>
                  <td className="px-5 py-4 font-medium text-[#2A1B1D]">{r.name}</td>
                  <td className="px-5 py-4 text-[#D4AF37] flex gap-0.5">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={13} fill="currentColor" />)}</td>
                  <td className="px-5 py-4 text-[#5C4A4D] max-w-md truncate">{r.text}</td>
                  <td className="px-5 py-4">
                    <button onClick={() => toggleReview(r)} data-testid={`toggle-review-${r.id}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${r.approved ? "bg-[#3E664E]/10 text-[#3E664E]" : "bg-[#8E3B46]/10 text-[#8E3B46]"}`}>
                      {r.approved ? <Eye size={13} /> : <EyeOff size={13} />} {r.approved ? "Shown" : "Hidden"}
                    </button>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => remove("reviews", r.id)} data-testid={`delete-review-${r.id}`} className="p-2 rounded-lg hover:bg-[#8E3B46]/10 text-[#8E3B46]"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editor && <ContentEditor type={editor.type} initial={editor.item} onClose={() => setEditor(null)} onSave={(f) => save(editor.type, f)} />}
    </div>
  );
}

function stripId(p) {
  const { id, ...rest } = p;
  return rest;
}

function Grid({ children, onAdd, addLabel }) {
  return (
    <div className="mt-6">
      <button onClick={onAdd} data-testid="content-add-btn" className="inline-flex items-center gap-2 rounded-full bg-[#8E3B46] px-5 py-3 text-sm font-medium text-[#FDFBF7] hover:bg-[#722F38] transition-colors mb-5">
        <Plus size={16} /> {addLabel}
      </button>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">{children}</div>
    </div>
  );
}

function Card({ image, title, sub, onEdit, onDelete, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#2A1B1D]/8 overflow-hidden group" data-testid={testid}>
      <div className="h-32 bg-[#F4EFEA] overflow-hidden">{image && <img src={image} alt={title} className="h-full w-full object-cover" />}</div>
      <div className="p-4">
        <p className="font-medium text-[#2A1B1D] text-sm truncate">{title}</p>
        {sub && <p className="text-xs text-[#8E3B46] mt-0.5">{sub}</p>}
        <div className="mt-3 flex gap-2">
          <button onClick={onEdit} className="flex-1 rounded-lg border border-[#2A1B1D]/12 py-1.5 text-xs text-[#5C4A4D] hover:border-[#8E3B46] flex items-center justify-center gap-1"><Pencil size={13} /> Edit</button>
          <button onClick={onDelete} className="rounded-lg border border-[#8E3B46]/30 px-3 py-1.5 text-xs text-[#8E3B46] hover:bg-[#8E3B46]/10"><Trash2 size={13} /></button>
        </div>
      </div>
    </div>
  );
}

function ContentEditor({ type, initial, onClose, onSave }) {
  const [form, setForm] = useState(() => {
    if (type === "offers") return { id: initial.id, title: initial.title || "", description: initial.description || "", discount: initial.discount || "", image: initial.image || "" };
    if (type === "packages") return { id: initial.id, name: initial.name || "", includes: initial.includes || [], price: initial.price ?? "", image: initial.image || "" };
    return { id: initial.id, url: initial.url || "", order: initial.order ?? 0 };
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-[#FDFBF7] border border-[#D4AF37]/30 p-0 gap-0" data-testid="content-editor">
        <DialogTitle className="sr-only">Edit content</DialogTitle>
        <DialogDescription className="sr-only">Manage website content item.</DialogDescription>
        <div className="px-7 pt-7 pb-4 border-b border-[#D4AF37]/20">
          <h3 className="font-display text-2xl text-[#2A1B1D] capitalize">{form.id ? "Edit" : "Add"} {type.slice(0, -1)}</h3>
        </div>
        <div className="px-7 py-6 space-y-4 max-h-[55vh] overflow-y-auto">
          {type === "offers" && (
            <>
              <F label="Title"><input value={form.title} onChange={(e) => set("title", e.target.value)} className={cls} data-testid="ce-title" /></F>
              <F label="Discount label"><input value={form.discount} onChange={(e) => set("discount", e.target.value)} className={cls} data-testid="ce-discount" placeholder="e.g. 20% OFF" /></F>
              <F label="Description"><textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className={cls} data-testid="ce-desc" /></F>
              <F label="Image URL"><input value={form.image} onChange={(e) => set("image", e.target.value)} className={cls} data-testid="ce-image" /></F>
            </>
          )}
          {type === "packages" && (
            <>
              <F label="Name"><input value={form.name} onChange={(e) => set("name", e.target.value)} className={cls} data-testid="ce-name" /></F>
              <F label="Includes (comma separated)"><textarea rows={2} value={Array.isArray(form.includes) ? form.includes.join(", ") : form.includes} onChange={(e) => set("includes", e.target.value)} className={cls} data-testid="ce-includes" /></F>
              <F label="Price (₹)"><input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className={cls} data-testid="ce-price" /></F>
              <F label="Image URL"><input value={form.image} onChange={(e) => set("image", e.target.value)} className={cls} data-testid="ce-image" /></F>
            </>
          )}
          {type === "gallery" && (
            <>
              <F label="Image URL"><input value={form.url} onChange={(e) => set("url", e.target.value)} className={cls} data-testid="ce-url" /></F>
              <F label="Order"><input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className={cls} data-testid="ce-order" /></F>
            </>
          )}
        </div>
        <div className="px-7 py-5 border-t border-[#D4AF37]/20 flex justify-end gap-3 bg-[#F4EFEA]/60">
          <button onClick={onClose} className="rounded-full border border-[#2A1B1D]/15 px-6 py-2.5 text-sm">Cancel</button>
          <button onClick={() => onSave(form)} data-testid="ce-save" className="rounded-full bg-[#8E3B46] px-7 py-2.5 text-sm font-medium text-[#FDFBF7] hover:bg-[#722F38]">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <label className="block"><span className="text-xs font-medium text-[#5C4A4D] mb-1.5 block">{label}</span>{children}</label>;
}
